import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import ProductCard from './components/ProductCard';
import { useTheme } from '@/context/ThemeContext';
import type { Product } from '@/mocks/products';
import { useProductCatalog } from '@/context/ProductCatalogContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

type Category = 'all' | 'lithium' | 'agm' | 'gel';

const categories: { value: Category; label: string; icon: string }[] = [
  { value: 'all', label: 'All Products', icon: 'ri-grid-line' },
  { value: 'lithium', label: 'Lithium LiFePO4', icon: 'ri-flashlight-line' },
  { value: 'agm', label: 'AGM Series', icon: 'ri-battery-2-charge-line' },
  { value: 'gel', label: 'Gel Series', icon: 'ri-drop-line' },
];

const catMeta: Record<Category, { title: string; desc: string; keywords: string }> = {
  all: {
    title: 'Marine Battery Catalog | Lithium, AGM & Gel | Stingray',
    desc: 'Browse the full Stingray marine battery lineup — lithium LiFePO4, AGM and gel series. Premium deep-cycle marine batteries for every vessel, trusted in 50+ countries.',
    keywords: 'marine battery catalog, lithium marine battery, AGM marine battery, gel marine battery, deep cycle battery, Stingray battery lineup',
  },
  lithium: {
    title: 'Lithium LiFePO4 Marine Batteries | Stingray',
    desc: 'Stingray lithium LiFePO4 marine batteries — 2000+ charge cycles, 10-year design life, IP67 waterproof. The ultimate upgrade for serious boaters worldwide.',
    keywords: 'lithium marine battery, LiFePO4 marine battery, lithium deep cycle battery, marine lithium battery 100ah 200ah',
  },
  agm: {
    title: 'AGM Marine Batteries | Maintenance-Free Deep Cycle | Stingray',
    desc: 'Stingray AGM marine batteries — maintenance-free, spill-proof, dual-purpose. Reliable deep-cycle performance at an accessible price for boaters worldwide.',
    keywords: 'AGM marine battery, sealed marine battery, maintenance free marine battery, dual purpose marine battery',
  },
  gel: {
    title: 'Gel Marine Batteries | Electronics-Safe Deep Cycle | Stingray',
    desc: 'Stingray gel marine batteries — ultra-stable voltage for sensitive electronics, fish finders and navigation systems. Zero maintenance, no gassing.',
    keywords: 'gel marine battery, gel deep cycle battery, electronics safe marine battery, stable voltage marine battery',
  },
};

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const catParam = searchParams.get('cat') as Category | null;
  const [activeCategory, setActiveCategory] = useState<Category>(catParam || 'all');
  const { theme } = useTheme();
  const { products } = useProductCatalog();
  const { settings } = useBadgeVisibility();
  const light = theme === 'light';

  useEffect(() => {
    if (catParam && ['lithium', 'agm', 'gel'].includes(catParam)) {
      setActiveCategory(catParam);
    } else if (!catParam) {
      setActiveCategory('all');
    }
  }, [catParam]);

  const filtered: Product[] =
    activeCategory === 'all'
      ? products
      : products.filter((p) => p.category === activeCategory);

  const productsSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Stingray Battery Catalog',
    description: 'Full lineup of Stingray marine batteries including Lithium LiFePO4, AGM and Gel series',
    url: 'https://stingray-marine.com/products',
    itemListElement: products.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.name,
      url: `https://stingray-marine.com/products/${p.id}`,
    })),
  };

  const handleCategory = (cat: Category) => {
    setActiveCategory(cat);
    if (cat === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ cat });
    }
  };

  const seo = catMeta[activeCategory];

  const bg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const headerBg = light ? 'bg-gray-100' : 'bg-[#0D1B2A]';
  const headingColor = light ? 'text-gray-900' : 'text-white';
  const bodyColor = light ? 'text-gray-600' : 'text-white/60';
  const breadcrumbColor = light ? 'text-gray-400' : 'text-white/30';
  const breadcrumbActive = light ? 'text-gray-600' : 'text-white/60';
  const filterInactive = light
    ? 'bg-white border border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50'
    : 'bg-[#1C2B3A] text-white/60 hover:text-white hover:bg-[#232F3E]';
  const emptyColor = light ? 'text-gray-400' : 'text-white/40';

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300`}>
      <SeoHead
        title={seo.title}
        description={seo.desc}
        keywords={seo.keywords}
        canonical={`https://stingray-marine.com/products${activeCategory !== 'all' ? `?cat=${activeCategory}` : ''}`}
        schema={productsSchema}
      />
      <Navbar />

      {/* Page Header */}
      <header className={`relative pt-32 pb-16 ${headerBg} overflow-hidden transition-colors duration-300`}>
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20navy%20ocean%20water%20texture%20deep%20sea%20background%20minimal%20dark%20blue%20waves%20pattern%20professional%20moody%20atmosphere%20high%20resolution%20seamless%20background&width=1440&height=400&seq=products-header-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 w-full px-6 md:px-10">
          <nav aria-label="Breadcrumb" className={`flex items-center gap-2 text-xs ${breadcrumbColor} mb-4`}>
            <a href="/" className="hover:text-[var(--accent)] transition-colors">Home</a>
            <i className="ri-arrow-right-s-line"></i>
            <span className={breadcrumbActive}>Products</span>
          </nav>
          {settings.productBadges && (
            <span className="inline-block text-xs font-bold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4" style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', color: 'var(--accent)' }}>
              Product Catalog
            </span>
          )}
          <h1 className={`text-4xl md:text-5xl font-black ${headingColor} mb-4`}>
            Stingray Battery Lineup
          </h1>
          <p className={`${bodyColor} text-lg max-w-2xl`}>
            Choose from our full range of marine batteries — engineered for performance, reliability, and longevity on the water.
          </p>
        </div>
      </header>

      {/* Filter + Grid */}
      <main className="w-full px-6 md:px-10 pb-20">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-10 pt-8">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => handleCategory(cat.value)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.value
                  ? 'text-white'
                  : filterInactive
              }`}
              style={activeCategory === cat.value ? { backgroundColor: 'var(--accent)' } : {}}
            >
              <i className={cat.icon}></i>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className={`text-center py-20 ${emptyColor}`}>
            <i className="ri-inbox-line text-5xl mb-4 block"></i>
            <p>No products found in this category.</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
